import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import translateServerErrorIntoFriendlyMessage from "components/discussions/form/translateServerErrorIntoFriendlyMessage";
import Editor from "components/editor/Editor";
import type { NewTopicData } from "components/forum/form";
import { newTopicDataShape } from "components/forum/form";
import InlineErrorSpan from "components/shared/form/InlineErrorSpan";
import { Form, FormField, FormMessage } from "components/ui/Form";
import { Input } from "components/ui/Input";
import CreatePostMutation from "lib/graphql/mutations/engagement/CreatePostMutation";
import type {
  ICreatePostMutation,
  ICreatePostMutationVariables,
} from "lib/graphql/mutations/engagement/__generated__/CreatePostMutation.generated";
import PostsQuery from "lib/graphql/queries/engagement/PostsQuery";
import { useRouter } from "next/router";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import type { IPostContextType } from "types/graphqlTypes";

interface CreatePostFormProps {
  contextKey?: string | null;
  contextType: IPostContextType;
  onClose: () => void;
}

export default function CreatePostForm({ contextKey = null, contextType, onClose }: CreatePostFormProps) {
  const { push } = useRouter();
  const intl = useIntl();

  const [createPost] = useMutation<ICreatePostMutation, ICreatePostMutationVariables>(CreatePostMutation, {
    refetchQueries: [
      {
        query: PostsQuery,
        variables: {
          contextKey,
          contextType,
          limit: 30,
          cursor: null,
        },
      },
    ],
    onCompleted: onClose,
  });

  const form = useForm<NewTopicData>({
    mode: "onSubmit",
    resolver: zodResolver(newTopicDataShape(intl)),
    defaultValues: {
      title: "",
      bodyAsJson: "",
      bodyAsPlainText: "",
    },
  });

  const {
    formState: { errors, touchedFields, isSubmitting },
  } = form;

  const handleSubmit = async (values: NewTopicData) => {
    try {
      const { data } = await createPost({
        variables: {
          contextKey,
          contextType,
          bodyAsJson: values.bodyAsJson,
          bodyAsPlainText: values.bodyAsPlainText,
          title: values.title,
        },
      });

      if (data && data.createPost) {
        push(`/forum/${data.createPost.shortId}`);
      }
    } catch (err) {
      form.setError("root.serverError", {
        message: translateServerErrorIntoFriendlyMessage(
          err,
          intl,
          intl.formatMessage({
            defaultMessage: "Não foi possível enviar sua mensagem agora. Tente novamente mais tarde.",
            id: "AsTfN9",
          }),
        ),
      });
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="grid h-full w-full gap-y-5">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => {
            return (
              <div className="space-y-2">
                <Input
                  {...field}
                  height={44}
                  type="text"
                  spellCheck={false}
                  autoComplete="off"
                  placeholder="Título"
                  name="title"
                  className="w-full"
                />

                <FormMessage />
              </div>
            );
          }}
        />

        <Editor
          className="h-fit w-full"
          onChange={(bodyAsJson, bodyAsPlainText) => {
            form.setValue("bodyAsJson", bodyAsJson);
            form.setValue("bodyAsPlainText", bodyAsPlainText);
          }}
          onSubmit={() => {
            form.handleSubmit(handleSubmit)();
          }}
        />

        {touchedFields.bodyAsJson && errors.bodyAsJson && (
          <InlineErrorSpan>{errors.bodyAsJson.message || ""}</InlineErrorSpan>
        )}

        {touchedFields.bodyAsPlainText && errors.bodyAsPlainText && (
          <InlineErrorSpan>{errors.bodyAsPlainText.message || ""}</InlineErrorSpan>
        )}

        <FormMessage asRootError={true} />

        <div className="flex w-full justify-end space-x-4">
          <button
            type="button"
            className="flex h-10 items-center justify-center space-x-2 rounded-md border border-brand bg-white px-4 font-bold text-brand"
            disabled={isSubmitting}
            onClick={() => {
              form.reset();
              onClose();
            }}
          >
            <FormattedMessage defaultMessage="Cancelar" id="nZLeaQ" />
          </button>

          <button
            type="submit"
            className="flex h-10 items-center justify-center space-x-2 rounded-md border border-transparent bg-brand px-4 font-bold text-white"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <FormattedMessage defaultMessage="Aguarde..." id="VZazm7" />
            ) : (
              <FormattedMessage defaultMessage="Salvar" id="DFil5M" />
            )}
          </button>
        </div>
      </form>
    </Form>
  );
}
